import ErrorIcon from '~shared/assets/icons/error.svg?react';
import InfoIcon from '~shared/assets/icons/info.svg?react';
import SuccessIcon from '~shared/assets/icons/success.svg?react';
import WarningIcon from '~shared/assets/icons/warning.svg?react';

import { Typography } from '@breezeventures/ui-kit/typography';
import cn from 'classnames';

export type InlineAlertType = 'info' | 'success' | 'warning' | 'error';

interface Props {
  type: InlineAlertType;
  title?: string;
  children?: React.ReactNode;
  showIcon?: boolean;
  className?: string;
}

type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

const typeConfig = {
  info: {
    bgClass: 'bg-info',
    accentClass: 'bg-info-accent',
    iconClass: 'text-info-accent',
    Icon: InfoIcon,
  },
  success: {
    bgClass: 'bg-positive-1',
    accentClass: 'bg-positive-accent',
    iconClass: 'text-positive-accent',
    Icon: SuccessIcon,
  },
  warning: {
    bgClass: 'bg-warning',
    accentClass: 'bg-warning-accent',
    iconClass: 'text-warning-accent',
    Icon: WarningIcon,
  },
  error: {
    bgClass: 'bg-negative-1',
    accentClass: 'bg-negative-accent',
    iconClass: 'text-negative-accent',
    Icon: ErrorIcon,
  },
} satisfies Record<
  InlineAlertType,
  {
    bgClass: string;
    accentClass: string;
    iconClass: string;
    Icon: IconComponent;
  }
>;

export const InlineAlert = ({
  type,
  title,
  children,
  showIcon = true,
  className,
}: Props) => {
  const { bgClass, accentClass, iconClass, Icon } = typeConfig[type];

  return (
    <div
      role="alert"
      className={cn(
        'relative flex items-start gap-3 overflow-hidden rounded-2xl px-4 py-3',
        bgClass,
        className
      )}
    >
      <span
        aria-hidden="true"
        className={cn('absolute inset-y-0 inset-s-0 w-1', accentClass)}
      />
      {showIcon && (
        <span
          aria-hidden="true"
          className={cn(
            'flex shrink-0 items-center',
            title ? 'h-6' : 'h-5',
            iconClass
          )}
        >
          <Icon className="size-5" />
        </span>
      )}
      <div className="flex flex-col gap-1">
        {title && (
          <Typography.Text tag="div" size="m" weight="medium">
            {title}
          </Typography.Text>
        )}
        {children && (
          <Typography.Text tag="div" size="s">
            {children}
          </Typography.Text>
        )}
      </div>
    </div>
  );
};
